import { isEmpty } from 'lodash';
import { addHours, isAfter, isEqual, isToday } from 'date-fns';
import { Advertiser, Agency, Brand, Environment, Line, RouteFrameCode } from 'components/common/types/Deal.types';
import { CodeNameModel, FileList } from 'components/common/types';
import {
  DirectSalesCampaignDealLevelDefinition,
  DirectSalesCampaignDealLineRequest,
  DirectSalesCampaignType,
  DirectSalesExclusionsRequest,
  DirectSalesFilterGroupCriteria,
  DirectSalesLine,
  DirectSalesLineObjective,
  DirectSalesMediaType,
  DirectSalesProductCategoryExclusivityType,
  DirectSalesSweep,
  DirectSalesTargetCriteria,
  DirectSalesTransformedDealLineRequest,
} from 'components/common/types/DirectSalesCampaign.types';
import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';
import { PatternOption } from 'components/common/DaypartTargeting/DaypartTargeting.types';
import { DirectSalesCampaignRequest } from 'components/common/types/DirectSalesCampaignRequest.types';
import { SelectedSegment } from 'components/common/types/Segment.types';
import { getDateInRequestFormat } from 'utils/dateUtil';
import { isAllSelectedFullDays } from 'components/common/DaypartTargeting/utils';
import { DEFAULT_CRITERIA } from 'consts/targetCriteria';
import { DirectSalesExclusionFlag, DirectSalesExclusions } from 'components/common/types/DirectSalesDeal.types';
import { POI } from 'components/common/types/Planner.types';
import { DEFAULT_START_DATE_OFFSET } from 'store/dealConfig/consts';
import { formatStartDateWithoutMilliseconds, formatEndDateWithoutMilliseconds } from 'utils/schedule';
import { resolveDirectSalesPricingModeForApi } from 'utils/resolvePublisherDefaultDirectSalesPricingMode';
import { ceilHour } from 'store/dealManagement/utils';
import { transformRangePattern, transformWeeklyPattern } from './transformDirectSalesDaypartRangePattern';
import { mergeFilters } from './transformFilters';
import {
  transformDirectSalesObjectivesTarget,
  transformTargetCriteriaValue,
} from './transformDirectSalesTargetsAndObjectives';

export const hasFilters = ({
  dealLineFormats,
  locations,
  poi,
  tags,
  channels,
  mediaType,
  listFiles,
  routeFrameCodes,
  mapRouteFrameCodes,
  networks,
  segments,
}: {
  dealLineFormats: CodeNameModel[];
  locations: CodeNameModel[];
  poi: POI[];
  tags: CodeNameModel[];
  channels: Environment[];
  mediaType?: DirectSalesMediaType;
  listFiles?: FileList[];
  routeFrameCodes?: RouteFrameCode[];
  mapRouteFrameCodes?: RouteFrameCode[];
  networks: NetworkCriteriaType[];
  segments?: SelectedSegment;
}): boolean => {
  if (dealLineFormats?.length) return true;

  if (locations?.length) return true;

  if (poi?.length) return true;

  if (tags?.length) return true;

  if (channels?.length) return true;

  if (mediaType) return true;

  if (listFiles?.length) return true;

  if (routeFrameCodes?.length) return true;

  if (mapRouteFrameCodes?.length) return true;

  if (networks?.length) return true;

  if (segments?.selectedSegment) return true;

  return false;
};

export const transformDirectSalesDealRequest = ({
  name,
  advertiser,
  brand,
  productCategory,
  agency,
  specialist,
  salesPerson,
  salesTeam,
  adminPerson,
  productCategoryExclusivity,
  exclusions,
  duplicationExclusion,
  juxtapositionExclusion,
  specialistBlockExclusion,
  agencyBlockExclusion,
  advertiserBlockExclusion,
}: {
  name?: string;
  advertiser: Advertiser;
  brand: Brand;
  productCategory: CodeNameModel;
  agency?: Agency;
  specialist?: CodeNameModel;
  salesPerson: CodeNameModel | null;
  salesTeam: CodeNameModel | null;
  adminPerson?: CodeNameModel | null;
  productCategoryExclusivity: DirectSalesProductCategoryExclusivityType;
  duplicationExclusion: DirectSalesExclusionFlag;
  exclusions: DirectSalesExclusions;
  juxtapositionExclusion: DirectSalesExclusionFlag;
  specialistBlockExclusion: DirectSalesExclusionFlag;
  agencyBlockExclusion: DirectSalesExclusionFlag;
  advertiserBlockExclusion: DirectSalesExclusionFlag;
}): DirectSalesExclusionsRequest & DirectSalesCampaignDealLevelDefinition => {
  return {
    name: name || '',
    campaignGroup: DirectSalesCampaignType.STANDARD,
    campaignType: DirectSalesCampaignType.STANDARD,
    advertiserId: advertiser?.code,
    brandId: brand?.code,
    productCategory: productCategory?.code,
    agencyId: agency?.code || '',
    specialistId: specialist?.code || '',
    ...(salesPerson ? { salesPerson } : {}),
    ...(salesTeam ? { salesTeam } : {}),
    ...(adminPerson ? { adminPerson } : {}),
    productCategoryExclusivity,
    exclusions: {
      organisations: exclusions.organisations.map((org) => org.code),
      productCategories: exclusions.productCategories.map((category) => category.code),
    },
    duplicationExclusion,
    juxtapositionExclusion,
    specialistBlockExclusion,
    agencyBlockExclusion,
    advertiserBlockExclusion,
  };
};

export const transformObjectivesRequest = (objectives: DirectSalesLineObjective[]): DirectSalesTargetCriteria[] => {
  const targetObjectives: DirectSalesTargetCriteria[] = [];

  objectives.forEach(
    ({
      budget,
      frames,
      impressions,
      name,
      objectiveGroupId,
      locations,
      formats,
      tags,
      poi,
      frameList: { listFiles, routeFrameCodes, mapRouteFrameCodes },
    }) => {
      const filter = mergeFilters({
        dealLineFormats: formats,
        locations,
        poi,
        tags,
        channels: [],
        listFiles,
        routeFrameCodes,
        mapRouteFrameCodes,
      });

      if (budget.value && objectiveGroupId) {
        const budgetObjective = transformDirectSalesObjectivesTarget({
          ...budget,
          name,
          criteriaType: DirectSalesFilterGroupCriteria.BUDGET_TARGET,
          objectiveGroupId,
          filter,
        });

        targetObjectives.push(budgetObjective);
      }

      if (frames.value && objectiveGroupId) {
        const framesObjective = transformDirectSalesObjectivesTarget({
          ...frames,
          name,
          criteriaType: DirectSalesFilterGroupCriteria.FRAME_TARGET,
          objectiveGroupId,
          filter,
        });

        targetObjectives.push(framesObjective);
      }

      if (impressions.value && objectiveGroupId) {
        const impressionsObjective = transformDirectSalesObjectivesTarget({
          ...impressions,
          name,
          criteriaType: DirectSalesFilterGroupCriteria.VOLUME_TARGET,
          objectiveGroupId,
          filter,
        });

        targetObjectives.push(impressionsObjective);
      }
    },
  );

  return targetObjectives;
};

export const transformDirectSalesDealLineRequest = ({
  id,
  name,
  startDate,
  endDate,
  floatingRange,
  filter,
  impressions,
  budget,
  frames,
  objectives,
  budgetCriteria,
  framesCriteria,
  impressionsCriteria,
  networkCriteria,
  sot,
  sotObjectiveId,
  sweep,
  selectedDays,
  patternRepeatType,
  pricingMode,
  productTemplate,
  productLineReference,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: DirectSalesCampaignDealLineRequest): DirectSalesTransformedDealLineRequest => {
  const targetCriteria: DirectSalesTargetCriteria[] = [];

  if (budget) {
    const budgetTarget: DirectSalesTargetCriteria = {
      objectiveId: budgetCriteria.objectiveId ?? null,
      criteriaType: DirectSalesFilterGroupCriteria.BUDGET_TARGET,
      value: transformTargetCriteriaValue(budget, budgetCriteria),
    };

    targetCriteria.push(budgetTarget);
  }

  if (frames) {
    const frameTarget: DirectSalesTargetCriteria = {
      objectiveId: framesCriteria.objectiveId ?? null,
      criteriaType: DirectSalesFilterGroupCriteria.FRAME_TARGET,
      value: transformTargetCriteriaValue(frames, framesCriteria),
    };

    targetCriteria.push(frameTarget);
  }

  if (impressions) {
    const impressionsTarget: DirectSalesTargetCriteria = {
      objectiveId: impressionsCriteria.objectiveId ?? null,
      criteriaType: DirectSalesFilterGroupCriteria.VOLUME_TARGET,
      value: transformTargetCriteriaValue(impressions, impressionsCriteria),
    };

    targetCriteria.push(impressionsTarget);
  }

  if (sot) {
    const sotTarget: DirectSalesTargetCriteria = {
      objectiveId: sotObjectiveId ?? null,
      criteriaType: DirectSalesFilterGroupCriteria.SOT_TARGET,
      value: transformTargetCriteriaValue(Math.round(sot * 100) / 10000, DEFAULT_CRITERIA),
    };

    targetCriteria.push(sotTarget);
  }

  if (networkCriteria?.length) {
    networkCriteria.forEach((criteria) => {
      const networkTarget: DirectSalesTargetCriteria = {
        objectiveId: criteria.objectiveId ?? null,
        criteriaType: DirectSalesFilterGroupCriteria.NETWORK_TARGET,
        value: {
          networkName: criteria.name,
          target: Number(criteria.target),
          priority: Number(criteria.priority.code),
          networkId: criteria.code,
          greenTolerance: criteria.greenTolerance / 100,
          orangeTolerance: criteria.orangeTolerance / 100,
        },
      };

      targetCriteria.push(networkTarget);
    });
  }

  if (objectives?.length) {
    targetCriteria.push(...transformObjectivesRequest(objectives));
  }

  const dealLine = {
    id,
    name,
    templateId: productTemplate?.id || undefined,
    templateName: productTemplate?.id ? productTemplate.name : undefined,
    range: {
      startDate,
      endDate,
      ...(floatingRange && floatingRange.floatingStartDate && floatingRange.floatingEndDate
        ? {
            floatingRange,
          }
        : { floatingRange: null }),
    },
    ...(filter ? { filter } : {}),
    ...(sweep ? { sweep } : { sweep: DirectSalesSweep.NONE }),
    targetCriteria,
    pricingMode: resolveDirectSalesPricingModeForApi({ pricingMode, networkCriteria }),
    ...(productLineReference !== undefined ? { productLineReference } : {}),
  };

  const selectedDaysAllFullDays = isAllSelectedFullDays(selectedDays);

  if (!selectedDaysAllFullDays && !isEmpty(selectedDays)) {
    const isRepeated = patternRepeatType !== PatternOption.NO_REPEAT;
    const rangePattern = isRepeated ? transformWeeklyPattern(selectedDays) : transformRangePattern(selectedDays);

    return {
      ...dealLine,
      rangePattern,
    };
  }

  return dealLine;
};

export const transformDirectSalesMultiDealLinesRequest = (
  lines: DirectSalesCampaignDealLineRequest[],
): {
  dealLines: DirectSalesTransformedDealLineRequest[];
} => {
  return {
    dealLines: lines.map((line) => transformDirectSalesDealLineRequest(line)),
  };
};

export const transformDirectSalesWithLineFormRequest = ({
  deal: dealFormData,
  lines,
}: {
  deal: {
    name?: string;
    advertiser: Advertiser;
    brand: Brand;
    productCategory: CodeNameModel;
    agency?: Agency;
    specialist?: CodeNameModel;
    salesPerson: CodeNameModel | null;
    salesTeam: CodeNameModel | null;
    adminPerson?: CodeNameModel | null;
    productCategoryExclusivity: DirectSalesProductCategoryExclusivityType;
    exclusions: DirectSalesExclusions;
    duplicationExclusion: DirectSalesExclusionFlag;
    juxtapositionExclusion: DirectSalesExclusionFlag;
    specialistBlockExclusion: DirectSalesExclusionFlag;
    agencyBlockExclusion: DirectSalesExclusionFlag;
    advertiserBlockExclusion: DirectSalesExclusionFlag;
  };
  lines: DirectSalesCampaignDealLineRequest[];
}): DirectSalesCampaignRequest => {
  const deal = transformDirectSalesDealRequest(dealFormData);
  const dealLines = transformDirectSalesMultiDealLinesRequest(lines);

  return {
    campaignDefinition: {
      ...deal,
      ...dealLines,
    },
  };
};

export const transformStartDate = (date: Date | null, offset = 0): Date | null => {
  if (!date) return null;

  const minStartDate = ceilHour(addHours(new Date(), offset));
  const isAfterMinDate = isAfter(date, minStartDate);
  const isEqualMinDate = isEqual(date, minStartDate);

  if (!isAfterMinDate && !isEqualMinDate) return null;

  if (!isToday(date) && isAfterMinDate) return new Date(date.setHours(0, 0, 0));

  return date;
};

export const transformAllLineData = (
  {
    lines,
    startDateOffset,
  }: {
    lines: Partial<Line>[];
    startDateOffset?: number;
  } = { lines: [], startDateOffset: DEFAULT_START_DATE_OFFSET },
): DirectSalesCampaignDealLineRequest[] => {
  return lines.map(
    ({
      lineId,
      name,
      startDate,
      endDate,
      floatingRange = null,
      tags,
      routeFrameCodes,
      mapRouteFrameCodes,
      impressions,
      frames,
      budget,
      impressionsCriteria,
      framesCriteria,
      budgetCriteria,
      networkCriteria,
      dealLineFormats,
      mediaType,
      locations,
      sot,
      sotObjectiveId,
      sweep,
      environments,
      selectedDays,
      listFiles,
      patternLength,
      patternRepeatType,
      state,
      lockState,
      pricingMode,
      objectives,
      poi,
      productTemplate,
      productLineReference,
      segment,
    }: DirectSalesLine) => {
      const lineStartDate = lineId
        ? (startDate as Date | null)
        : transformStartDate(startDate as Date | null, startDateOffset);

      return {
        name,
        id: lineId,
        startDate: lineStartDate ? formatStartDateWithoutMilliseconds(lineStartDate) : null,
        endDate:
          endDate && lineStartDate
            ? formatEndDateWithoutMilliseconds(new Date((endDate as Date).setHours(23, 59, 59)), lineStartDate)
            : null,
        ...(floatingRange && floatingRange.floatingStartDate && floatingRange.floatingEndDate
          ? {
              floatingRange: {
                floatingStartDate: getDateInRequestFormat(floatingRange.floatingStartDate as Date, [0, 0, 0], {
                  milliseconds: false,
                }),
                floatingEndDate: getDateInRequestFormat(floatingRange.floatingEndDate as Date, [23, 59, 59], {
                  milliseconds: false,
                }),
              },
            }
          : { floatingRange: null }),
        ...(hasFilters({
          dealLineFormats,
          locations,
          poi,
          tags,
          channels: environments,
          mediaType,
          listFiles,
          routeFrameCodes,
          mapRouteFrameCodes,
          networks: networkCriteria,
          segments: segment?.selectedSegments,
        })
          ? {
              filter: mergeFilters({
                dealLineFormats,
                locations,
                poi,
                tags,
                channels: environments,
                mediaType,
                listFiles,
                routeFrameCodes,
                mapRouteFrameCodes,
                networks: networkCriteria,
                segments: segment?.selectedSegments,
              }),
            }
          : {}),
        impressions,
        budget,
        frames,
        impressionsCriteria,
        budgetCriteria,
        framesCriteria,
        networkCriteria,
        objectives,
        sot,
        sotObjectiveId,
        sweep,
        selectedDays,
        patternLength,
        patternRepeatType,
        state,
        lockState,
        pricingMode: resolveDirectSalesPricingModeForApi({ pricingMode, networkCriteria }),
        productTemplate,
        ...(productLineReference !== undefined ? { productLineReference } : {}),
      };
    },
  );
};
